import React, { useContext, useEffect, useState } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import Swal from 'sweetalert2';

import { ReCAPTCHAAPIKEY, emailPattern } from '../../helpers/callejeritosImages';
import { eliminarLogotipo, LOGOTIPO_ACCEPT, subirLogotipo, validarLogotipo } from '../../helpers/logotipo';
import { ActualizarUsuarioResp } from '../../interfaces/usuariosInterfaces';
import { UserContext } from '../../context/UserContext';

import kw2pApiPublic from '../../api/kw2pPublic';

import Logotipo from '../../logotipo.svg';


export const RegisterForm = () => {
    
    const { languaje } = useContext(UserContext);

    // shouldUnregister: los campos de AC/Proyecto se desmontan al elegir "No"; sin esto RHF conserva
    // sus valores y los valida, disparando falsos errores de un bloque que ya no se ve.
    const { formState: { errors, isSubmitting }, control, handleSubmit, reset, register, setValue, watch } = useForm<IFormInputs>({ shouldUnregister: true });
    const correo = useWatch({name:'correo', control});
    const esAc = watch('es_ac_proyecto_es');

    const logoFile = watch('es_ac_proyecto')?.logotipo?.[0];
    const [logoPreview, setLogoPreview] = useState('');
    useEffect(() => {
        if (!logoFile) { setLogoPreview(''); return; }
        const url = URL.createObjectURL(logoFile);
        setLogoPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [logoFile]);

    const mostrarRegistroExitoso = () => {
        reset();
        Swal.fire({
            title: 'Registro completado',
            text: 'Valida tú correo electronico para continuar',
            footer: 'Revisa tú bandeja de entrada y/o correo no deseado (spam) para validar y activar tu cuenta y sigue las instrucciones',
            imageUrl: Logotipo,
            imageHeight: 70, 
            imageWidth: 325
        });
    };

    const onSubmit: SubmitHandler<IFormInputs> = async ({nombre, correo, telefono, ciudad, pais, es_ac_proyecto}) => {
        let logoFallido = false;
        try {

            const payload: any = {
                nombre,
                correo,
                telefono: telefono.replace(/[^0-9.]/g, ''),
                roles: ['portal'],
                credenciales: [],
                domicilio: {
                    ciudad,
                    pais
                }
            };

            // Paso 1: Registrar usuario (sin es_ac_proyecto aún)
            const data = (await kw2pApiPublic.post<ActualizarUsuarioResp>('/usuarios:actualizar', payload)).data;
            const { data: { usuario } } = data
debugger
            // Paso 2: Si pertenece a AC/Proyecto, subir logo y actualizar
            try {
                if (es_ac_proyecto && typeof es_ac_proyecto === 'object') {
                    const dirLogotipo = `logotipos/${usuario.id}`;
                    let logotipoUrl = '';

                    if (es_ac_proyecto.logotipo?.length) {
                        await eliminarLogotipo(dirLogotipo);
                        logotipoUrl = await subirLogotipo(es_ac_proyecto.logotipo[0], dirLogotipo);
                    }

                    await kw2pApiPublic.post('/usuarios:actualizar', {
                        id: usuario.id,
                        nombre,
                        correo,
                        es_ac_proyecto: {
                            nombre: es_ac_proyecto.nombre,
                            logotipo: logotipoUrl
                        }
                    });
                }
            } catch (logoErr) {
                logoFallido = true;
                throw logoErr;
            }

            mostrarRegistroExitoso();

        } catch (err: any) {
            if (logoFallido) {
                Swal.fire({
                    icon: 'error',
                    title: 'Lo sentimos ocurrio un error...',
                    text: 'Hubo un error con el logotipo. Una vez que inicies sesión y entres a tu perfil, podrás intentarlo de nuevo.'
                });
                return;
            }

            const msg = err?.response?.data?.errors ?? err?.response?.data?.message ?? err?.message ?? 'Error inesperado';
            Swal.fire({
                icon: 'error',
                title: 'Lo sentimos ocurrio un error...',
                text: typeof msg === 'string' ? msg : JSON.stringify(msg).replace(/[{}]/g, '')
            });
        }
    };

    const formatPhone = (val: string) => {
        const phone = val.replace(/[^0-9.]/g, '');
        const telformated = phone.slice(0,3) + (phone.length > 3?("-" + phone.slice(3,6)):'') + (phone.length > 6?("-" + phone.slice(6)):'');
        setValue('telefono', telformated, { shouldValidate: true, shouldDirty: true });
    };
    const rulesValidateEqual = (val:string, msg:string) => (value:string) => value === val || msg

    const onChange = (value: any) => {
        setValue('captcha', value);
    };

    const disableCaptcha = !watch('captcha');

    return (
        <form onSubmit={ handleSubmit(onSubmit) } autoComplete="none">
            <div className="row g-3">
                <div className="col-12">
                    <input id="nombre" className="form-control" placeholder={ languaje === 'ES' ? 'Nombre completo' : 'Full name' } autoComplete="none"
                    { ...register('nombre', { required: languaje === 'ES' ? 'El nombre es requerido': 'The name is required' }) } required/>
                    { errors.nombre && <small className="text-danger">{ errors.nombre.message }</small> }
                </div>
                <div className="col-12 col-md-4 mt-4">
                    <input id="correo" className="form-control" type="email" placeholder={ languaje === 'ES' ? 'Correo electrónico' : 'Email' }
                    autoComplete="none"
                    { ...register('correo', { 
                        required: languaje === 'ES' ? 'El correo es requerido': 'The email is required',
                        pattern: { value : emailPattern, message: languaje === 'ES' ? 'Ingresa un correo válido': 'Enter a valid email' } }) } required/>
                    { errors.correo && <small className="text-danger">{ errors.correo.message }</small> }
                </div>
                <div className="col-12 col-md-4 mt-4">
                    <input id="correo_conf" className="form-control" type="email" placeholder={ languaje === 'ES' ? 'Validar Correo electrónico' : 'Validate Email' }
                    autoComplete="none"
                    { ...register('correo_conf', { 
                        required: languaje === 'ES' ? 'El correo es requerido': 'The email is required',
                        validate: rulesValidateEqual(correo,'El correo no coinciden',),
                        pattern: { value : emailPattern, message: languaje === 'ES' ? 'Ingresa un correo válido': 'Enter a valid email' } }) } required/>
                    { errors.correo_conf && <small className="text-danger">{ errors.correo_conf.message }</small> }
                </div>
                <div className="col-12 col-md-4 mt-4">
                    <input id="telefono" className="form-control" placeholder={ languaje === 'ES' ? 'Teléfono' : 'Phone' } autoComplete="none"
                    { ...register('telefono', { 
                        required: languaje === 'ES' ? 'El teléfono es requerido': 'The phone is required',
                        maxLength: { value : 12, message: languaje === 'ES' ? 'Ingresa un teléfono válido': 'Enter a valid celphone' },
                        minLength: { value : 12, message: languaje === 'ES' ? 'Ingresa un teléfono válido': 'Enter a valid celphone' }
                    }) }
                        onChange={ (e) => formatPhone(e.target.value) } maxLength={12} required/>
                    { errors.telefono && <small className="text-danger">{ errors.telefono.message }</small> }
                </div>
                <div className="col-12 col-md-6 mt-4">
                    <input id="ciudad" className="form-control" placeholder={ languaje === 'ES' ? 'Ciudad' : 'City' } autoComplete="none"
                    { ...register('ciudad', { required: languaje === 'ES' ? 'La ciudad es requerida': 'The city is required' }) } required/>
                    { errors.ciudad && <small className="text-danger">{ errors.ciudad.message }</small> }
                </div>
                <div className="col-12 col-md-6 mt-4">
                    <input id="pais" className="form-control" placeholder={ languaje === 'ES' ? 'Pais' : 'Country' } autoComplete="none"
                    { ...register('pais', { required: languaje === 'ES' ? 'El pais es requerido': 'The country is required' }) } required/>
                    { errors.pais && <small className="text-danger">{ errors.pais.message }</small> }
                </div>
                <div className="col-12 mt-4">
                    <label htmlFor="es_ac_proyecto" className="d-block" style={{ cursor: 'pointer', fontWeight: 'normal' }}>
                        { languaje === 'ES' ? '¿Pertenece a una AC o Proyecto?' : 'Do you belong to an AC (Animal Welfare Org) or Project?' }
                    </label>
                    <div className="form-check form-check-inline">
                        <input id="es_ac_proyecto_si" type="radio" className="form-check-input" value="si"
                        { ...register('es_ac_proyecto_es', {
                            required: languaje === 'ES' ? 'Selecciona una opción' : 'Please select an option'
                        }) } />
                        <label className="form-check-label" htmlFor="es_ac_proyecto_si">{ languaje === 'ES' ? 'Sí' : 'Yes' }</label>
                    </div>
                    <div className="form-check form-check-inline">
                        <input id="es_ac_proyecto_no" type="radio" className="form-check-input" value="no" { ...register('es_ac_proyecto_es') } />
                        <label className="form-check-label" htmlFor="es_ac_proyecto_no">{ languaje === 'ES' ? 'No' : 'No' }</label>
                    </div>
                    { errors.es_ac_proyecto_es && <small className="text-danger d-block">{ errors.es_ac_proyecto_es.message }</small> }
                </div>
                { esAc === 'si' &&
                <>
                    <div className="col-12 mt-2">
                        <input id="es_ac_proyecto_nombre" className="form-control" placeholder={ languaje === 'ES' ? 'Nombre de AC o Proyecto' : 'AC or Project name' } autoComplete="none"
                        { ...register('es_ac_proyecto.nombre', {
                            required: languaje === 'ES' ? 'El nombre de la AC o Proyecto es requerido': 'The AC or Project name is required'
                        }) } />
                        { (errors as any).es_ac_proyecto?.nombre && <small className="text-danger">{ (errors as any).es_ac_proyecto.nombre.message }</small> }
                    </div>
                    <div className="col-12 mt-2">
                        { logoPreview && <img src={ logoPreview } alt="logotipo" className="img-fluid d-block mb-2" style={{ maxHeight: 80 }} /> }
                        <div className="custom-file">
                            <input id="es_ac_proyecto_logotipo" type="file" className="custom-file-input" accept={ LOGOTIPO_ACCEPT }
                            { ...register('es_ac_proyecto.logotipo', {
                                validate: (files: any) => {
                                    if (!files?.length) return languaje === 'ES' ? 'El logotipo es requerido' : 'The logo is required';
                                    const razon = validarLogotipo(files[0]);
                                    if (razon === 'tipo') return languaje === 'ES' ? 'El logotipo debe ser PNG, JPG o SVG' : 'The logo must be PNG, JPG or SVG';
                                    if (razon === 'tamano') return languaje === 'ES' ? 'El logotipo no debe exceder 2MB' : 'The logo must not exceed 2MB';
                                    return true;
                                }
                            }) } />
                            <label className="custom-file-label" htmlFor="es_ac_proyecto_logotipo">{ logoFile?.name ?? (languaje === 'ES' ? 'Subir logotipo' : 'Upload logo') }</label>
                        </div>
                        <small className="text-muted">{ languaje === 'ES' ? 'Formatos permitidos: PNG, JPG, SVG. Máximo 2MB.' : 'Allowed formats: PNG, JPG, SVG. Max 2MB.' }</small>
                        <br />
                        { (errors as any).es_ac_proyecto?.logotipo && <small className="text-danger">{ (errors as any).es_ac_proyecto.logotipo.message }</small> }
                    </div>
                </>
                }
            </div>
            <div className="d-flex flex-column mt-2">
                <ReCAPTCHA sitekey={ ReCAPTCHAAPIKEY } onChange={onChange} hl={ languaje === 'ES' ? 'es-419' : 'en' }/>
                <Link to="/login" className="login_link">{ languaje === 'ES' ? '¿Ya tienes una cuenta? Inicia sesión' : 'Do you already have an account? Log in' }</Link>
                <button className="btn login_btn btn-block" disabled={ disableCaptcha || isSubmitting } type="submit">{languaje === 'ES' ? 'Registrarme' : 'Register'}</button>
            </div>
        </form>
    )
}

interface IFormInputs {
    nombre: string;
    correo: string;
    correo_conf: string;
    telefono: string;
    ciudad: string;
    pais: string;
    es_ac_proyecto_es?: 'si' | 'no';
    es_ac_proyecto?: { nombre: string; logotipo?: FileList };
    captcha : string;
}
